import { sendDomainLabel } from './SendDomains';
import { fileKeysOf } from './SendText';
import { _isNotEmpty } from '@/littledash';
import type { SendCoercionWarning, SendFileDetection, SendValidationResult } from '@/model/Agent.model';

/** One file's suspected Excel coercion, keyed and named however the place showing it names files. */
export interface SendCoercionFile {
  key: string;
  name: string;
  warnings: Array<SendCoercionWarning>;
  omitted: number;
}

interface Scanned {
  coercion_warnings?: Array<SendCoercionWarning>;
  coercion_warnings_omitted?: number;
}

/**
 * The files with at least one warning, in order. A clean scan carries an empty list and a file stored
 * before files were scanned carries none; neither is listed, and neither is ever said to be clean.
 */
const suspectFilesOf = <T extends Scanned>(
  files: ReadonlyArray<T>,
  identify: (file: T, index: number) => Pick<SendCoercionFile, 'key' | 'name'>
): Array<SendCoercionFile> =>
  files.reduce<Array<SendCoercionFile>>((acc, file, index) => {
    const warnings = file.coercion_warnings ?? [];
    const omitted = file.coercion_warnings_omitted ?? 0;
    if (_isNotEmpty(warnings) || omitted > 0) {
      acc.push({ ...identify(file, index), warnings, omitted });
    }
    return acc;
  }, []);

/** The confirm step's suspect files, named as uploaded. */
export const detectionCoercionFilesOf = (detections: ReadonlyArray<SendFileDetection>): Array<SendCoercionFile> =>
  suspectFilesOf(detections, (detection) => ({
    key: detection.attachment_api_id,
    name: detection.original_filename,
  }));

/** The report's suspect files, named by domain as the rest of the report names them. */
export const resultCoercionFilesOf = (files: ReadonlyArray<SendValidationResult>): Array<SendCoercionFile> => {
  const keys = fileKeysOf(files);
  return suspectFilesOf(files, (file, index) => ({ key: keys[index], name: sendDomainLabel(file.domain_code) }));
};
